import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import RichTextEditor from "@/admin/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = ["Customer Success", "Engineering", "Product", "Sales", "Marketing", "Operations", "Other"];
const TYPES = ["Full-time", "Part-time", "Contract", "Internship"];

const EMPTY_JOB = {
  slug: "",
  title: "",
  department: "",
  location: "",
  type: "Full-time",
  summary: "",
  body_html: "",
  apply_email: "",
  order: 0,
  published: true,
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

export default function AdminJobEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug || slug === "new";

  const [form, setForm] = useState(EMPTY_JOB);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/jobs/${slug}`);
        setForm({
          slug: data.slug,
          title: data.title || "",
          department: data.department || "",
          location: data.location || "",
          type: data.type || "Full-time",
          summary: data.summary || "",
          body_html: data.body_html || "",
          apply_email: data.apply_email || "",
          order: data.order ?? 0,
          published: data.published !== false,
        });
      } catch {
        toast.error("Job not found.");
        navigate("/admin/jobs");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (isNew && !form.slug.trim()) {
      toast.error("Slug is required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form, slug: form.slug || slugify(form.title) };
        await axios.post(`${API}/admin/jobs`, payload);
        toast.success("Job created.");
        navigate(`/admin/jobs/${payload.slug}`);
      } else {
        // Don't send slug as a PATCH field - it's the URL key.
        const { slug: _ignored, ...patch } = form;
        await axios.patch(`${API}/admin/jobs/${slug}`, patch);
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-sm text-slate-400" data-testid="admin-job-edit-loading">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-job-edit-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="px-5 lg:px-8 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between sticky top-0 bg-white z-10">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/jobs"
            data-testid="admin-job-edit-back"
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-base font-bold text-slate-900">
              {isNew ? "New Job" : form.title || "Edit Job"}
            </h1>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
              {isNew ? "Draft" : `/careers/${form.slug}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Link to={`/careers/${form.slug}`} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                size="sm"
                data-testid="admin-job-edit-preview"
                className="rounded-lg text-xs"
              >
                <Eye className="h-3.5 w-3.5 mr-1" /> Preview
              </Button>
            </Link>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-job-edit-save"
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs h-9"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : isNew ? "Create job" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Job Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => {
                const title = e.target.value;
                update({ title, slug: isNew && !form.slug ? slugify(title) : form.slug });
              }}
              placeholder="e.g. Senior Software Engineer"
              data-testid="admin-job-edit-title"
              className="mt-1.5 h-10"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">URL Slug *</Label>
            <Input
              value={form.slug}
              onChange={(e) => update({ slug: slugify(e.target.value) })}
              disabled={!isNew}
              placeholder="e.g. senior-software-engineer"
              data-testid="admin-job-edit-slug"
              className="mt-1.5 h-10 font-mono text-xs"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Becomes /careers/{form.slug || "<slug>"}. Cannot be changed after creation.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Summary (one or two sentences shown on the list page)</Label>
            <Textarea
              value={form.summary}
              onChange={(e) => update({ summary: e.target.value })}
              rows={2}
              placeholder="Help us build the next generation of Antera's cloud ERP and eCommerce platforms…"
              data-testid="admin-job-edit-summary"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Full description</Label>
            <p className="text-[11px] text-slate-400 mt-0.5 mb-2">
              Use headings (H2/H3), bullet lists, and bold text to structure responsibilities, qualifications, and benefits.
            </p>
            <RichTextEditor
              value={form.body_html}
              onChange={(html) => update({ body_html: html })}
              placeholder="Write the full job description…"
              testId="admin-job-edit-body"
            />
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-5">
          <div className="border border-slate-200 rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Visibility</h3>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => update({ published: e.target.checked })}
                data-testid="admin-job-edit-published"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-sm text-slate-700">Published (visible on /careers)</span>
            </label>
          </div>

          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Details</h3>

            <div>
              <Label className="text-xs text-slate-600">Department</Label>
              <select
                value={form.department}
                onChange={(e) => update({ department: e.target.value })}
                data-testid="admin-job-edit-department"
                className="mt-1 w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
              >
                <option value="">- Select -</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Location</Label>
              <Input
                value={form.location}
                onChange={(e) => update({ location: e.target.value })}
                placeholder="Onsite - Plano, TX"
                data-testid="admin-job-edit-location"
                className="mt-1 h-9 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Employment type</Label>
              <select
                value={form.type}
                onChange={(e) => update({ type: e.target.value })}
                data-testid="admin-job-edit-type"
                className="mt-1 w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Apply email (optional)</Label>
              <Input
                type="email"
                value={form.apply_email}
                onChange={(e) => update({ apply_email: e.target.value })}
                placeholder="careers@anterasoftware.com"
                data-testid="admin-job-edit-apply-email"
                className="mt-1 h-9 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">List order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => update({ order: Number(e.target.value) || 0 })}
                data-testid="admin-job-edit-order"
                className="mt-1 h-9 text-sm w-24"
              />
              <p className="text-[10px] text-slate-400 mt-1">Lower numbers appear first on /careers.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
